import { formatDate } from '@angular/common';
import { Pagination } from '../../../../models/pagination.model';
import { Resource } from '../../resource/models';
import { LiveClass, Meeting, MeetingProvider } from './live-class';
import { datetime, Frequency, RRule } from 'rrule';
import { DateTime } from 'luxon';
import { getAdjustedDate } from '@black/shared';
import { generateRandomShortId } from '../../../../util/helper.utils';
import {
  SUBJECT_OPTIONS_BY_AUDIENCE,
  TARGET_AUDIENCE_OPTIONS,
} from '../../../../util/sri-lanka-data';

export class MeetingDTO implements Meeting {
  id?: string;
  name: string;
  shareLinkId?: string;
  isRecursive: boolean;

  scheduledDate?: Date;
  recurrence?: {
    type: string;
    every: number;
    monthlyDay?: number;
    monthlyWeek?: number;
    monthlyWeekDay?: string;
    weeklyDays?: string[];
  };

  recurrenceType: string;
  startTime: string;
  endTime: string;
  timezone: string;
  startDate?: Date;
  endDate?: Date;
  provider?: MeetingProvider;
  directJoinUrl?: string;
  price?: number;

  constructor(formEntity: IMeetingEntity) {
    this.id = formEntity.id;
    this.name = formEntity.title;
    this.shareLinkId = formEntity.shareLinkId ?? generateRandomShortId();
    this.isRecursive = formEntity.repeat !== 'ONCE';
    this.recurrenceType = formEntity.repeat;
    this.provider = formEntity.provider;
    this.directJoinUrl = formEntity.directJoinUrl;
    this.price = formEntity.price;

    // Common fields
    this.startTime = formEntity.from;
    this.endTime = formEntity.to;
    this.timezone = formEntity.timezone;

    if (formEntity.repeat === 'ONCE') {
      // One-time meeting
      this.scheduledDate = formEntity.onceDate
        ? new Date(formEntity.onceDate)
        : undefined;
    } else {
      // Recurring meeting
      const isMonthly = formEntity.repeat === 'MONTHLY';
      const isOption1 =
        !formEntity.onTheMonthOption ||
        formEntity.onTheMonthOption === 'option-1';
      const isOption2 = formEntity.onTheMonthOption === 'option-2';
      const isWeekly = formEntity.repeat === 'WEEKLY';

      this.recurrence = {
        type: formEntity.repeat,
        every: formEntity.every,
        monthlyDay:
          isMonthly && isOption1 && formEntity.OnTheMonthDay
            ? +formEntity.OnTheMonthDay
            : undefined,
        monthlyWeek:
          isMonthly && isOption2 && formEntity.onTheMonthWeek
            ? this.generateMonthlyWeek(formEntity.onTheMonthWeek)
            : undefined,
        monthlyWeekDay:
          isMonthly && isOption2 && formEntity.OnTheMonthWeekDay
            ? formEntity.OnTheMonthWeekDay.toUpperCase()
            : undefined,
        weeklyDays:
          isWeekly && formEntity.onTheWeek
            ? this.generateWeekDays(formEntity.onTheWeek)
            : undefined,
      };
    }
  }

  private generateWeekDays(weekDayForm?: object) {
    if (!weekDayForm || typeof weekDayForm !== 'object') return [];
    const weekDays: string[] = [];

    for (const [key, value] of Object.entries(weekDayForm)) {
      if (value) {
        weekDays.push(key.toUpperCase());
      }
    }
    return weekDays;
  }

  private generateMonthlyWeek(weekOrder: string) {
    switch (weekOrder) {
      case 'FIRST':
        return 1;
        break;
      case 'SECOND':
        return 2;
        break;
      case 'THIRD':
        return 3;
        break;
      case 'FOURTH':
        return 4;
        break;

      case 'LAST':
        return -1;
        break;

      default:
        return undefined;
        break;
    }
  }
}

export class MeetingUtility {
  meeting: Meeting;
  startDate: Date;
  endDate: Date;

  rrule?: RRule;

  constructor(meeting: Meeting, startDate: Date, endDate: Date) {
    this.startDate = new Date(startDate);
    this.endDate = new Date(endDate);
    this.meeting = meeting;

    const [startHour, startMinutes] = this.meeting.startTime
      .split(':')
      .map((timeInString) => Number.parseInt(timeInString));

    // Only create RRule for recurring meetings
    if (this.meeting.recurrenceType !== 'ONCE' && this.meeting.recurrence) {
      const weekdays = this.getWeekDay(
        this.meeting.recurrence.weeklyDays &&
          this.meeting.recurrence.weeklyDays.length !== 0
          ? this.meeting.recurrence.weeklyDays
          : [this.meeting.recurrence.monthlyWeekDay ?? ''],
      );

      this.rrule = new RRule({
        freq: this.getFreq(this.meeting.recurrenceType),
        byweekday: weekdays.length ? weekdays : undefined,
        bymonthday:
          this.meeting.recurrenceType === 'MONTHLY'
            ? this.meeting.recurrence.monthlyDay
            : undefined,
        bysetpos:
          this.meeting.recurrenceType === 'MONTHLY'
            ? this.meeting.recurrence.monthlyWeek
            : undefined,
        interval: this.meeting.recurrence.every,
        tzid: this.meeting.timezone,
        dtstart: datetime(
          this.startDate.getUTCFullYear(),
          this.startDate.getUTCMonth() + 1,
          this.startDate.getUTCDate(),
          startHour,
          startMinutes,
        ),
        until: datetime(
          this.endDate.getUTCFullYear(),
          this.endDate.getUTCMonth() + 1,
          this.endDate.getUTCDate(),
          23,
          59,
          59,
        ),
      });
    } else if (
      this.meeting.recurrenceType === 'ONCE' &&
      this.meeting.scheduledDate
    ) {
      // For ONCE meetings, use the scheduledDate
      this.startDate = new Date(this.meeting.scheduledDate);
    }
  }

  toRRule(): RRule | null {
    return this.rrule || null;
  }

  getStartDate() {
    return getAdjustedDate({
      date: this.startDate,
      time: this.meeting.startTime || '00:00',
      tzid: this.meeting.timezone || 'Asia/Colombo',
    });
  }

  toRRuleDescription(): string {
    // For one-time meetings, provide a custom description
    if (this.meeting.recurrenceType === 'ONCE') {
      return 'Once';
    }
    return this.rrule?.toText() || '';
  }

  toDescription(): string {
    if (this.meeting.recurrenceType === 'ONCE' && this.meeting.scheduledDate) {
      // Handle both Date objects and string dates
      const date =
        typeof this.meeting.scheduledDate === 'string'
          ? DateTime.fromISO(this.meeting.scheduledDate)
          : DateTime.fromJSDate(this.meeting.scheduledDate);

      if (date.isValid) {
        return `Once on ${date.toLocaleString(DateTime.DATE_MED)}`;
      }
      return 'Once';
    }
    return this.rrule?.toText() || '';
  }

  getAllRecurrences() {
    if (!this.rrule) return [];
    return this.rrule
      .all()
      .map((date) =>
        DateTime.fromJSDate(date)
          .toUTC()
          .setZone('local', { keepLocalTime: true })
          .toJSDate(),
      );
  }

  getAllRecurrencesBetween(from: Date, to: Date) {
    if (!this.rrule) return [];
    const fromDt = datetime(
      from.getUTCFullYear(),
      from.getUTCMonth() + 1,
      from.getUTCDate(),
      from.getUTCHours(),
      from.getUTCMinutes(),
      from.getUTCSeconds(),
    );
    const toDt = datetime(
      to.getUTCFullYear(),
      to.getUTCMonth() + 1,
      to.getUTCDate(),
      to.getUTCHours(),
      to.getUTCMinutes(),
      to.getUTCSeconds(),
    );
    return this.rrule
      .between(fromDt, toDt, true)
      .map((date) =>
        DateTime.fromJSDate(date)
          .toUTC()
          .setZone('local', { keepLocalTime: true })
          .toJSDate(),
      );
  }

  getSessionInterval() {
    const [startHour, startMinutes] = this.meeting.startTime
      .split(':')
      .map((timeInString) => Number.parseInt(timeInString));
    const [endHour, endMinutes] = this.meeting.endTime
      .split(':')
      .map((timeInString) => Number.parseInt(timeInString));

    const diffInMinutes =
      endHour * 60 + endMinutes - (startHour * 60 + startMinutes);

    if (diffInMinutes < 0) {
      return (
        endHour * 60 + endMinutes + (24 * 60 - (startHour * 60 + startMinutes))
      );
    }

    return diffInMinutes;
  }

  getFreq(recurrenceType: string) {
    if (recurrenceType === 'WEEKLY') {
      return Frequency.WEEKLY;
    }
    if (recurrenceType === 'MONTHLY') {
      return Frequency.MONTHLY;
    }
    if (recurrenceType === 'DAILY') {
      return Frequency.DAILY;
    }
    return Frequency.YEARLY;
  }

  getWeekDay(weeklyDays: string[]) {
    const WeekDay = [];

    if (weeklyDays.includes('MONDAY')) {
      WeekDay.push(RRule.MO);
    }
    if (weeklyDays.includes('TUESDAY')) {
      WeekDay.push(RRule.TU);
    }
    if (weeklyDays.includes('WEDNESDAY')) {
      WeekDay.push(RRule.WE);
    }
    if (weeklyDays.includes('THURSDAY')) {
      WeekDay.push(RRule.TH);
    }
    if (weeklyDays.includes('FRIDAY')) {
      WeekDay.push(RRule.FR);
    }
    if (weeklyDays.includes('SATURDAY')) {
      WeekDay.push(RRule.SA);
    }
    if (weeklyDays.includes('SUNDAY')) {
      WeekDay.push(RRule.SU);
    }

    return WeekDay;
  }
}

export class MeetingEntity implements IMeetingEntity {
  id?: string;
  shareLinkId?: string;
  title: string;
  repeat: string;
  every: number;
  onceDate?: string;
  from: string;
  to: string;
  onTheMonthOption: string;
  OnTheMonthDay?: number | undefined;
  onTheMonthWeek?: string | undefined;
  OnTheMonthWeekDay?: string | undefined;
  onTheWeek: {
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
    sunday: boolean;
  };
  timezone: string;
  provider?: MeetingProvider;
  directJoinUrl?: string;
  price?: number;

  constructor(meeting: Meeting) {
    this.id = meeting.id;
    this.shareLinkId = meeting.shareLinkId;
    this.title = meeting.name;
    this.repeat = meeting.recurrenceType;
    this.provider = meeting.provider;
    this.directJoinUrl = meeting.directJoinUrl;
    this.price = meeting.price;

    // Common fields
    this.from = meeting.startTime;
    this.to = meeting.endTime;
    this.timezone = meeting.timezone;

    if (meeting.recurrenceType === 'ONCE') {
      // One-time meeting
      this.every = 1;
      if (meeting.scheduledDate) {
        const dt =
          typeof meeting.scheduledDate === 'string'
            ? DateTime.fromISO(meeting.scheduledDate)
            : DateTime.fromJSDate(meeting.scheduledDate);
        this.onceDate = dt.isValid ? dt.toFormat('yyyy-LL-dd') : undefined;
      } else {
        this.onceDate = undefined;
      }
      this.onTheMonthOption = 'option-1';
      this.onTheWeek = {
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false,
      };
    } else if (meeting.recurrence) {
      // Recurring meeting
      this.every = meeting.recurrence.every;
      this.onTheMonthOption = meeting.recurrence.monthlyDay
        ? 'option-1'
        : 'option-2';
      this.OnTheMonthDay = meeting.recurrence.monthlyDay;
      this.onTheMonthWeek = this.generateOnTheMonthWeek(
        meeting.recurrence.monthlyWeek,
      );
      this.OnTheMonthWeekDay = meeting.recurrence.monthlyWeekDay
        ? meeting.recurrence.monthlyWeekDay[0].toUpperCase() +
          meeting.recurrence.monthlyWeekDay.slice(1).toLowerCase()
        : '';
      this.onTheWeek = {
        monday: meeting.recurrence.weeklyDays?.includes('MONDAY') ?? false,
        tuesday: meeting.recurrence.weeklyDays?.includes('TUESDAY') ?? false,
        wednesday:
          meeting.recurrence.weeklyDays?.includes('WEDNESDAY') ?? false,
        thursday: meeting.recurrence.weeklyDays?.includes('THURSDAY') ?? false,
        friday: meeting.recurrence.weeklyDays?.includes('FRIDAY') ?? false,
        saturday: meeting.recurrence.weeklyDays?.includes('SATURDAY') ?? false,
        sunday: meeting.recurrence.weeklyDays?.includes('SUNDAY') ?? false,
      };
    } else {
      // Default values for unexpected case
      this.every = 1;
      this.onTheMonthOption = 'option-1';
      this.onTheWeek = {
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false,
      };
    }
  }

  private generateOnTheMonthWeek(week: number | undefined) {
    switch (week) {
      case 1:
        return 'FIRST';
        break;
      case 2:
        return 'SECOND';
        break;
      case 3:
        return 'THIRD';
        break;
      case 4:
        return 'FOURTH';
        break;

      case -1:
        return 'LAST';
        break;

      default:
        return undefined;
        break;
    }
  }
}

export interface LiveClassResponse extends Pagination {
  docs: LiveClass[];
}

export class LiveClassDTO implements ILiveClassDTO {
  title: string;
  details: {
    description: string;
    descriptionSummary: string;
    startDate: Date;
    endDate: Date;
    medium: string;
    classMode: 'ONLINE' | 'HYBRID';
    requirements?: string[];
    coverage?: string[];
  };
  settings: {
    maxStudents: number;
    isPublic: boolean;
    price: number;
    bannerImageResource: Resource | string;
    maxDuration: number;
  };
  meetings: Meeting[];
  targetAudience: string;
  subject: string;
  socialLinks: { whatsapp: string; telegram: string };

  constructor(formEntity: ILiveClassEntity, meetingEntities: IMeetingEntity[]) {
    this.title = formEntity.basic.title;
    this.details = {
      description: formEntity.basic.description,
      descriptionSummary: formEntity.basic.summary,
      startDate: new Date(formEntity.information.startDate),
      endDate: new Date(formEntity.information.endDate),
      medium: formEntity.information.medium,
      classMode: formEntity.information.classMode,
      requirements: formEntity.information.requirements.filter(
        (item) => item !== '',
      ),
      coverage: formEntity.information.coverage.filter((item) => item !== ''),
    };
    this.settings = {
      maxStudents: formEntity.settings.maxStudents,
      maxDuration: this.computeMaxDuration(meetingEntities),
      isPublic: formEntity.settings.isPublic,
      price: formEntity.settings.price,
      bannerImageResource: formEntity.settings.banner,
    };
    this.meetings = meetingEntities.map((entity) => new MeetingDTO(entity));
    this.targetAudience =
      formEntity.information.targetAudience === 'Other'
        ? formEntity.information.targetAudienceOther || ''
        : formEntity.information.targetAudience;
    this.subject =
      formEntity.information.subject === 'Other'
        ? formEntity.information.subjectOther || ''
        : formEntity.information.subject;
    this.socialLinks = formEntity.socialLinks;
  }

  private computeMaxDuration(meetingEntities: IMeetingEntity[]): number {
    if (!meetingEntities.length) return 0;
    return Math.max(
      ...meetingEntities.map((e) => {
        const [sh, sm] = e.from.split(':').map(Number);
        const [eh, em] = e.to.split(':').map(Number);
        let diff = eh * 60 + em - (sh * 60 + sm);
        if (diff < 0) diff += 24 * 60;
        return diff;
      }),
    );
  }
}

type ILiveClassDTO = Partial<LiveClass>;

export interface ILiveClassEntity {
  basic: {
    title: string;
    description: string;
    summary: string;
  };

  information: {
    startDate: string;
    endDate: string;
    medium: string;
    classMode: 'ONLINE' | 'HYBRID';
    targetAudience: string;
    targetAudienceOther: string;
    subject: string;
    subjectOther: string;
    requirements: string[];
    coverage: string[];
    tags: string;
  };

  settings: {
    maxStudents: number;
    isPublic: boolean;
    price: number;
    banner: string;
  };

  socialLinks: {
    whatsapp: string;
    telegram: string;
  };

  meetingsLength: number;
}

export class LiveClassEntity implements ILiveClassEntity {
  basic: { title: string; description: string; summary: string };
  information: {
    startDate: string;
    endDate: string;
    medium: string;
    classMode: 'ONLINE' | 'HYBRID';
    targetAudience: string;
    targetAudienceOther: string;
    subject: string;
    subjectOther: string;
    requirements: string[];
    coverage: string[];
    tags: string;
  };
  settings: {
    maxStudents: number;
    isPublic: boolean;
    price: number;
    banner: string;
  };

  socialLinks: { whatsapp: string; telegram: string };

  meetingsLength: number;

  constructor(liveClass: LiveClass) {
    this.basic = {
      title: liveClass.title,
      description: liveClass.details.description,
      summary: liveClass.details.descriptionSummary,
    };
    const isPredefinedAudience = TARGET_AUDIENCE_OPTIONS.includes(
      liveClass.targetAudience,
    );
    const resolvedAudience = isPredefinedAudience
      ? liveClass.targetAudience
      : 'Other';
    const subjectOptions =
      SUBJECT_OPTIONS_BY_AUDIENCE[
        resolvedAudience !== 'Other'
          ? resolvedAudience
          : liveClass.targetAudience
      ] ?? [];
    const isPredefinedSubject = subjectOptions.includes(liveClass.subject);
    this.information = {
      startDate: formatDate(liveClass.details.startDate, 'yyyy-MM-dd', 'en'),
      endDate: formatDate(liveClass.details.endDate, 'yyyy-MM-dd', 'en'),
      medium: liveClass.details.medium,
      classMode: liveClass.details.classMode || 'ONLINE',
      targetAudience: isPredefinedAudience ? liveClass.targetAudience : 'Other',
      targetAudienceOther: isPredefinedAudience ? '' : liveClass.targetAudience,
      subject: isPredefinedSubject ? liveClass.subject : 'Other',
      subjectOther: isPredefinedSubject ? '' : liveClass.subject,
      requirements: liveClass.details.requirements ?? [],
      coverage: liveClass.details.coverage ?? [],
      tags: '',
    };
    this.settings = {
      maxStudents: liveClass.settings.maxStudents,
      isPublic: liveClass.settings.isPublic,
      price: liveClass.settings.price,
      banner: this.getBannerId(liveClass.settings.bannerImageResource),
    };
    this.socialLinks = {
      whatsapp: liveClass.socialLinks?.whatsapp ?? '',
      telegram: liveClass.socialLinks?.telegram ?? '',
    };
    this.meetingsLength = liveClass.meetings.length;
  }

  private getBannerId(banner: Resource | string) {
    if (typeof banner !== 'string') {
      return banner._id;
    }

    return '';
  }
}

export interface IMeetingEntity {
  id?: string;
  shareLinkId?: string;
  title: string;
  repeat: string;
  every: number;
  onceDate?: string;
  from: string;
  to: string;
  onTheMonthOption?: string;
  OnTheMonthDay?: number;
  onTheMonthWeek?: string;
  OnTheMonthWeekDay?: string;
  onTheWeek?: {
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
    sunday: boolean;
  };
  timezone: string;
  provider?: MeetingProvider;
  directJoinUrl?: string;
  price?: number;
}
